if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          prevButton: this.querySelector('.slider-button--prev'), // Add prev button
          nextButton: this.querySelector('.slider-button--next'), // Add next button
          sliderCounterCurrent: this.querySelector('.slider-counter--current'), // Add counter
          sliderCounterTotal: this.querySelector('.slider-counter--total'), // Add total
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // Initialize button event listeners
        if (this.elements.prevButton) {
          this.elements.prevButton.addEventListener('click', this.navigateToPrevious.bind(this));
        }
        if (this.elements.nextButton) {
          this.elements.nextButton.addEventListener('click', this.navigateToNext.bind(this));
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      // Helper to get visible media items (excludes those with hide-media class)
      getVisibleMediaItems() {
        return Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]:not(.hide-media)'));
      }

      // Helper to get the current active media index
      getActiveMediaIndex() {
        const visibleItems = this.getVisibleMediaItems();
        return visibleItems.findIndex((item) => item.classList.contains('is-active'));
      }

      // Navigate to the previous visible media item
      navigateToPrevious() {
        const visibleItems = this.getVisibleMediaItems();
        if (visibleItems.length === 0) return;

        let currentIndex = this.getActiveMediaIndex();
        let nextIndex = currentIndex > 0 ? currentIndex - 1 : visibleItems.length - 1; // Loop to last item
        const nextMediaId = visibleItems[nextIndex].dataset.mediaId;

        this.setActiveMedia(nextMediaId, false);
        this.updateSliderCounter(nextIndex + 1, visibleItems.length);
      }

      // Navigate to the next visible media item
      navigateToNext() {
        const visibleItems = this.getVisibleMediaItems();
        if (visibleItems.length === 0) return;

        let currentIndex = this.getActiveMediaIndex();
        let nextIndex = currentIndex < visibleItems.length - 1 ? currentIndex + 1 : 0; // Loop to first item
        const nextMediaId = visibleItems[nextIndex].dataset.mediaId;

        this.setActiveMedia(nextMediaId, false);
        this.updateSliderCounter(nextIndex + 1, visibleItems.length);
      }

      // Update the slider counter
      updateSliderCounter(current, total) {
        if (this.elements.sliderCounterCurrent && this.elements.sliderCounterTotal) {
          this.elements.sliderCounterCurrent.textContent = current;
          this.elements.sliderCounterTotal.textContent = total;
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        const visibleItems = this.getVisibleMediaItems();
        const currentIndex = visibleItems.findIndex((item) => item.dataset.mediaId === event.detail.currentElement.dataset.mediaId);
        this.updateSliderCounter(currentIndex + 1, visibleItems.length);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          if (activeMedia.parentElement.firstChild !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
          }
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail?.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
        this.elements.thumbnails.querySelectorAll('button').forEach((element) => {
          element.removeAttribute('aria-current');
        });
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}